import React from 'react';
import { lineTotal } from '../../../lib/quantity';
import { useShopPayments } from '../hooks/useShopPayments';

const inputClass =
  'w-full min-h-touch border border-border-strong rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent';

// Mirrors backend money.deposit_amount, for display only: the server decides
// what the customer is actually asked to pay.
//   parts      = sum of line totals (each rounded to the cent) before discount and tax
//   percentage = percent of the total, rounded half up to the cent
// Either way never more than the total (a big discount can push it below the parts).
export const previewDeposit = ({ type, percent, parts, total }) => {
  if (type === 'parts') {
    const cents = (parts || []).reduce((sum, p) => sum + Math.round(lineTotal(p.quantity, p.unit_cost) * 100), 0);
    const totalCents = Math.max(0, Math.round(Number(((Number(total) || 0) * 100).toPrecision(12))));
    return Math.min(cents, totalCents) / 100;
  }
  if (type === 'percentage') {
    const p = parseFloat(percent);
    if (!(p > 0 && p <= 100)) return null;
    const totalCents = Math.round(Number(((Number(total) || 0) * 100).toPrecision(12)));
    return Math.round(Number((totalCents * p / 100).toPrecision(12))) / 100;
  }
  return 0;
};

// Estimates only. Hidden until the shop can actually take card payments.
const DepositControl = ({ editableInvoice, setEditableInvoice, editableParts, total }) => {
  const { account } = useShopPayments();
  if (!account || account.state !== 'ready' || !account.has_payment_tier) return null;

  const type = editableInvoice.deposit_type || 'none';
  const percent = editableInvoice.deposit_percent ?? '';
  const preview = type === 'none' ? null : previewDeposit({ type, percent, parts: editableParts, total });

  return (
    <div className="mb-6 border border-border-default rounded-md p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block text-sm font-medium text-ink">
          <span className="block mb-1">Deposit</span>
          <select
            aria-label="Deposit"
            value={type}
            onChange={(e) => setEditableInvoice((prev) => ({ ...prev, deposit_type: e.target.value }))}
            className={inputClass}
          >
            <option value="none">None</option>
            <option value="parts">Parts cost</option>
            <option value="percentage">Percentage</option>
          </select>
        </label>
        {type === 'percentage' && (
          <label className="block text-sm font-medium text-ink">
            <span className="block mb-1">Deposit percentage (%)</span>
            <input
              aria-label="Deposit percentage"
              type="number"
              inputMode="decimal"
              min="0.01"
              max="100"
              step="any"
              value={percent}
              onChange={(e) => setEditableInvoice((prev) => ({ ...prev, deposit_percent: e.target.value }))}
              className={inputClass}
            />
          </label>
        )}
      </div>
      {type !== 'none' && (
        <p className="text-sm text-ink mt-2" aria-live="polite">
          {preview === null ? 'Enter a percentage between 0 and 100' : `Deposit: $${preview.toFixed(2)}`}
        </p>
      )}
      <p className="text-small text-ink-muted mt-1">
        The customer pays this first from their estimate link. Save the estimate to apply it.
      </p>
    </div>
  );
};

export default DepositControl;
